'use client';

import Cookies from 'js-cookie';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';

import { ReferralType } from '../__generated-gql-types__/globalTypes';

import { LELAND_DOMAIN } from './constants';
import { LelandSessionStorage } from './storage';

export const CUSTOMER_REFERRAL_CREDIT = 5000;

export const REFERRAL_CODE_KEY = 'referralCode';
export const REFERRAL_TYPE_KEY = 'referralType';
const REFERRAL_TIMESTAMP_KEY = 'referralTimestamp';

const EXPIRES_DAYS = 14;

export const REFERRAL_TYPE_MAP: Record<ReferralType, string> = {
  [ReferralType.COACH_REFER_CUSTOMER]: '1',
  [ReferralType.COACH_REFER_COACH]: '2',
  [ReferralType.CUSTOMER_REFER_CUSTOMER]: '3',
  [ReferralType.CUSTOMER_REFER_COACH]: '4',
};

const REFERRAL_TYPE_MAP_REVERSE = Object.fromEntries(
  Object.entries(REFERRAL_TYPE_MAP).map(([key, value]) => [value, key]),
) as Record<string, ReferralType>;

export const getReferralTypeFromString = (
  param: Possible<string>,
): Optional<ReferralType> => {
  if (!param) return undefined;
  return REFERRAL_TYPE_MAP_REVERSE[param];
};

interface ReferralParameters {
  referralCode: string;
  referralType: ReferralType;
  source: 'query' | 'session' | 'cookie';
  storedAt: string;
}

export const useReferralParameters = (): Nullable<ReferralParameters> => {
  const searchParams = useSearchParams();

  const searchReferralCode = searchParams?.get(REFERRAL_CODE_KEY);
  const searchReferralType = getReferralTypeFromString(
    searchParams?.get(REFERRAL_TYPE_KEY),
  );

  useEffect(() => {
    if (searchReferralCode && searchReferralType) {
      // first touch attribution - if cookies exist, don't override them
      if (!Cookies.get(REFERRAL_CODE_KEY)) {
        const timestamp = new Date().toISOString();

        // Store in session storage for immediate use
        LelandSessionStorage.setItem(REFERRAL_CODE_KEY, searchReferralCode);
        LelandSessionStorage.setItem(REFERRAL_TYPE_KEY, searchReferralType);
        LelandSessionStorage.setItem(REFERRAL_TIMESTAMP_KEY, timestamp);

        // Store in cookie for persistence
        Cookies.set(REFERRAL_CODE_KEY, searchReferralCode, {
          domain: LELAND_DOMAIN,
          expires: EXPIRES_DAYS,
        });
        Cookies.set(REFERRAL_TYPE_KEY, searchReferralType, {
          domain: LELAND_DOMAIN,
          expires: EXPIRES_DAYS,
        });
        Cookies.set(REFERRAL_TIMESTAMP_KEY, timestamp, {
          domain: LELAND_DOMAIN,
          expires: EXPIRES_DAYS,
        });
      }
    }
  }, [searchReferralCode, searchReferralType]);

  return useMemo(() => {
    const storedParams = getReferralParameters();

    // If we have stored data, use it (first-touch attribution)
    if (storedParams?.referralCode) {
      return {
        ...storedParams,
        source:
          // If query params match stored params, credit query as source
          searchReferralCode === storedParams.referralCode &&
          searchReferralType === storedParams.referralType
            ? 'query'
            : storedParams.source,
      };
    }

    // No stored data, use query params if available
    if (searchReferralCode && searchReferralType) {
      return {
        referralCode: searchReferralCode,
        referralType: searchReferralType,
        source: 'query',
        storedAt: new Date().toISOString(),
      };
    }

    // Fallback to empty stored params
    return storedParams;
  }, [searchReferralCode, searchReferralType]);
};

const getReferralParameters = (): Nullable<ReferralParameters> => {
  const sessionReferralCode =
    LelandSessionStorage.getItem<string>(REFERRAL_CODE_KEY);
  const sessionReferralType =
    LelandSessionStorage.getItem<ReferralType>(REFERRAL_TYPE_KEY);
  const sessionTimestamp = LelandSessionStorage.getItem<string>(
    REFERRAL_TIMESTAMP_KEY,
  );

  if (sessionReferralCode && sessionReferralType && sessionTimestamp) {
    return {
      referralCode: sessionReferralCode,
      referralType: sessionReferralType,
      source: 'session',
      storedAt: sessionTimestamp,
    };
  }

  if (Cookies.get(REFERRAL_CODE_KEY) && Cookies.get(REFERRAL_TYPE_KEY)) {
    return {
      referralCode: Cookies.get(REFERRAL_CODE_KEY) ?? '',
      referralType: Cookies.get(REFERRAL_TYPE_KEY) as ReferralType,
      source: 'cookie',
      storedAt: Cookies.get(REFERRAL_TIMESTAMP_KEY) ?? '',
    };
  }

  return null;
};

export const clearReferralParameters = () => {
  Cookies.remove(REFERRAL_CODE_KEY);
  Cookies.remove(REFERRAL_TYPE_KEY);
  Cookies.remove(REFERRAL_TIMESTAMP_KEY);
  LelandSessionStorage.removeItem(REFERRAL_CODE_KEY);
  LelandSessionStorage.removeItem(REFERRAL_TYPE_KEY);
  LelandSessionStorage.removeItem(REFERRAL_TIMESTAMP_KEY);
};
